import { createMutationObserver } from "./utils";

export class TransactionalFields {
    /**
     * TransactionalFields class.
     *
     * @param {Object} options - The options object.
     * @param {HTMLElement} options.container - The container element.
     * @param {Function} options.onRowAdded - The callback function.
     * @param {Function} options.onRowRemoved - The callback function.
     */
    constructor({ container, onRowAdded, onRowRemoved }) {
        this.container = container;
        this.onRowAdded = onRowAdded;
        this.onRowRemoved = onRowRemoved;
        this.observer = createMutationObserver({
            target: container,
            handleMutations: this.handleMutations.bind(this),
        });
    }

    /**
     * Gets the input elements.
     *
     * @returns {NodeListOf<HTMLInputElement>} The input elements.
     */
    get inputs() {
        return this.container.querySelectorAll("input");
    }

    /**
     * Toggles the visibility of the transactional fields.
     *
     * @param {boolean} isVisible - Whether the transactional fields should be visible.
     */
    toggleVisibility(isVisible = true) {
        this.container.classList.toggle("d-none", !isVisible);
    }

    /**
     * Sets the required attribute for the input elements.
     *
     * @param {boolean} isRequired - Whether the input elements should be required.
     */
    setRequired(isRequired = true) {
        const inputElements = this.container.querySelectorAll("input");
        inputElements.forEach((input) => {
            input.required = isRequired;

            // Update data-validate for custom validation system
            let validation = input.getAttribute("data-validate") || "";
            if (isRequired && !validation.includes("required")) {
                validation = ("required " + validation).trim();
            } else if (!isRequired) {
                validation = validation.replace(/\brequired\b/g, "").trim();
            }
            input.setAttribute("data-validate", validation);
        });
    }

    /**
     * Cleans up the observer.
     */
    cleanup() {
        this.observer.disconnect();
    }

    /**
     * Handles the mutations.
     *
     * @param {MutationRecord[]} mutations - The mutations.
     */
    handleMutations(mutations) {
        mutations.forEach((mutation) => {
            const addedRow = mutation.addedNodes.item(0);
            if (mutation.type === "childList" && addedRow?.tagName === "TR") {
                this.onRowAdded(addedRow);
            }

            const removedRow = mutation.removedNodes.item(0);
            if (mutation.type === "childList" && removedRow?.tagName === "TR") {
                this.onRowRemoved(removedRow);
            }
        });
    }
}
